import { useEffect, useId, useRef, useState } from 'react'
import { Film, Image as ImageIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SkillCoverView, SkillFileView } from '../../generated/directorSkillTypes'
import { readDirectorSkillAsset } from '../../api/directorSkills'
import { createNadDebug } from '../../utils/debugLog'

const coverLog = createNadDebug('dw:skill-cover-preview:media')

interface SkillCoverMediaProps {
  cover?: SkillCoverView | null
  title: string
  mode: 'thumbnail' | 'preview' | 'hover' | 'detail'
  className?: string
}

function useAuthenticatedObjectUrl(path: string | null): {
  url: string | null
  loading: boolean
  error: boolean
} {
  const [state, setState] = useState<{
    path: string | null
    url: string | null
    loading: boolean
    error: boolean
  }>(() => ({ path, url: null, loading: Boolean(path), error: false }))

  useEffect(() => {
    let active = true
    let objectUrl: string | null = null
    setState({ path, url: null, loading: Boolean(path), error: false })
    if (!path) {
      return
    }
    void readDirectorSkillAsset(path, 'cover')
      .then((blob) => {
        if (!active) return
        objectUrl = URL.createObjectURL(blob)
        setState({ path, url: objectUrl, loading: false, error: false })
      })
      .catch(() => {
        if (active) {
          setState({ path, url: null, loading: false, error: true })
        }
      })
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path])

  if (state.path !== path) return { url: null, loading: Boolean(path), error: false }
  return { url: state.url, loading: state.loading, error: state.error }
}

export function SkillCoverMedia({ cover, title, mode, className = '' }: SkillCoverMediaProps) {
  const { t } = useTranslation('chat')
  const traceId = useId()
  const preview = useAuthenticatedObjectUrl(cover?.preview_url ?? null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoSize, setVideoSize] = useState<{ url: string; width: number; height: number } | null>(null)
  const playsVideo = cover?.kind === 'video' && (mode === 'hover' || mode === 'detail')
  const playable = useAuthenticatedObjectUrl(playsVideo ? cover.content_url : null)
  const intrinsic = mode === 'detail' || mode === 'hover'
  const videoStyle = videoSize && videoSize.url === playable.url && videoSize.height > 0
    ? { width: Math.min(videoSize.width, (mode === 'detail' ? 120 : 240) * videoSize.width / videoSize.height), aspectRatio: `${videoSize.width} / ${videoSize.height}` }
    : undefined
  const mediaClass = intrinsic
    ? `block h-auto w-auto max-w-full object-contain ${mode === 'detail' ? 'max-h-[120px]' : 'max-h-60'}`
    : 'h-full w-full object-cover'
  const detailClass = intrinsic ? 'mx-auto w-fit max-w-full shrink-0' : ''

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    return () => {
      video.pause()
      video.removeAttribute('src')
      video.load()
      coverLog({ trace_id: traceId, event: 'cover:playback-released', title, mode, reason: 'unmounted-or-replaced', paused: video.paused, source_removed: !video.hasAttribute('src') })
    }
  }, [playable.url, title, mode, traceId])
  const containerClass = `relative block overflow-hidden rounded-xl border border-border bg-surface-0 ${detailClass} ${className}`

  if (!cover) return null

  if (playsVideo && playable.url) {
    return (
      <span className={containerClass} data-testid="skill-cover-video">
        <video
          key={`${playable.url}:${mode}:${title}`}
          ref={videoRef}
          controls={mode === 'detail'}
          autoPlay={mode === 'hover'}
          muted={mode === 'hover'}
          loop={mode === 'hover'}
          playsInline
          onPlaying={(event) => coverLog({ trace_id: traceId, event: 'cover:playback-started', title, mode, muted: event.currentTarget.muted, loop: event.currentTarget.loop, plays_inline: event.currentTarget.playsInline })}
          onLoadedMetadata={(event) => {
            const video = event.currentTarget
            setVideoSize({ url: video.src, width: video.videoWidth, height: video.videoHeight })
            coverLog({ trace_id: traceId, event: 'cover:dimensions-loaded', title, mode, media_kind: 'video', width: video.videoWidth, height: video.videoHeight })
          }}
          style={videoStyle}
          preload="metadata"
          poster={preview.url ?? undefined}
          src={playable.url}
          className={mediaClass}
          aria-label={t('skillPicker.coverVideo', { name: title })}
        />
      </span>
    )
  }

  if (preview.url) {
    return (
      <span className={containerClass} data-testid="skill-cover-preview">
        <img
          src={preview.url}
          alt={t('skillPicker.coverAlt', { name: title })}
          onLoad={(event) => coverLog({ trace_id: traceId, event: 'cover:dimensions-loaded', title, mode, media_kind: 'image', width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight, rendered_width: event.currentTarget.getBoundingClientRect().width, rendered_height: event.currentTarget.getBoundingClientRect().height })}
          className={mediaClass}
        />
        {cover.kind === 'video' && mode === 'preview' && (
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full border border-border bg-background/90 px-2 py-1 text-xxs text-foreground shadow-lg">
            <Film className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" /> {t('skillPicker.video')}
          </span>
        )}
      </span>
    )
  }

  return (
    <span
      className={`${containerClass} flex items-center justify-center text-text-muted`}
      aria-label={preview.error ? t('skillPicker.coverUnavailable', { name: title }) : t('skillPicker.coverLoading', { name: title })}
      data-testid={preview.error ? 'skill-cover-error' : 'skill-cover-loading'}
    >
      {cover.kind === 'video' ? (
        <Film className={`h-7 w-7 ${preview.loading ? 'animate-pulse' : ''}`} strokeWidth={1.5} />
      ) : (
        <ImageIcon className={`h-7 w-7 ${preview.loading ? 'animate-pulse' : ''}`} strokeWidth={1.5} />
      )}
    </span>
  )
}

export function skillFileViewForCover(cover: SkillCoverView, files: SkillFileView[]): SkillFileView | undefined {
  return files.find((file) => file.path === cover.file_path)
}
